import { convertHtmlToReact } from '@hedgedoc/html-to-react';
import { useInjection } from 'inversify-react';
import { useCallback, useEffect, useState } from 'react';

import { Image, Tooltip } from '@desygner/ui-common-components';

import { IConfigStoreInjector, IViewerStoreInjector, TYPES } from '../../../inversify.config';
import { StyledWrapper } from './Logo.style';

async function loadSVG(logoUrl: string) {
  try {
    const response = await fetch(logoUrl);

    if (!response.ok) {
      throw new Error(`Network response was not ok: ${response.statusText}`);
    }

    const text = await response.text();
    const parser = new DOMParser();
    const svg = parser.parseFromString(text, 'image/svg+xml').documentElement;

    return new XMLSerializer().serializeToString(svg);
  } catch (error) {
    console.error('Error loading SVG:', error);
    return null;
  }
}

export function Logo({ title, onClick }: { title: string; onClick: () => void }) {
  const [logo, setLogo] = useState<{
    source: string;
    type: 'svg' | 'image';
  } | null>(null);
  const useConfigStore = useInjection<IConfigStoreInjector>(TYPES.Store.Config);
  const useViewerStore = useInjection<IViewerStoreInjector>(TYPES.Store.Viewer);
  const logoUrl = useConfigStore((state) => state.brand.logoUrl);
  const isWorkspace = useViewerStore((state) => state.company?.is_workspace);
  const whiteLabelLogoUrl = useViewerStore((state) =>
    state.company && 'white_label_configuration' in state.company
      ? state.company.white_label_configuration.logo_url
      : null
  );

  useEffect(() => {
    if (whiteLabelLogoUrl && isWorkspace) {
      setLogo({
        source: whiteLabelLogoUrl,
        type: 'image',
      });
    } else if (logoUrl) {
      if (logoUrl.endsWith('.svg')) {
        loadSVG(logoUrl)
          .then((source) => {
            setLogo({
              source: source || logoUrl,
              type: source ? 'svg' : 'image',
            });
          })
          .catch(() => {
            setLogo({
              source: logoUrl,
              type: 'image',
            });
          });
      } else {
        setLogo({
          source: logoUrl,
          type: 'image',
        });
      }
    }
  }, [setLogo, logoUrl, isWorkspace, whiteLabelLogoUrl]);

  const handleOnClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      onClick();
    },
    [onClick]
  );

  if (!logo) return;

  return (
    <Tooltip text={title} visible={true} position={'bottom'}>
      <StyledWrapper data-testid="logo" isWorkspace={false} onClick={handleOnClick}>
        {logo.type === 'image' ? (
          <Image source={logo.source} alt={title} />
        ) : (
          convertHtmlToReact(logo.source)
        )}
      </StyledWrapper>
    </Tooltip>
  );
}
